<template>
  <AcquisitionEditStyled>
    <header>{{ $t('actions.updateAcquisition') }}</header>
    <AcquisitionManageMolecule
      :isLoading="false"
      :selectedOrganization="selectedOrganization"
      :selectedProduct="selectedProduct"
      :selectedProductTier="selectedProductTier"
      :selectedAcquisitionType="selectedAcquisitionType"
      :organizations="organizations"
      :productsAvailable="productsAvailable"
      :productsTierAvailable="productsTierAvailable"
      :acquisitionsTypes="acquisitionsTypes"
      :productsTierNotAvailable="productsTierNotAvailable"
      @update:selectOrganization="
        org => {
          selectedOrganizationId = org.id
        }
      "
      @update:selectProduct="
        p => {
          selectedProductId = p.id
        }
      "
      @update:selectProductTier="
        t => {
          selectedProductTierId = t.id
        }
      "
      @update:selectAcquisitionType="selectedAcquisitionType = $event"
    />
    <div class="action">
      <Button
        :label="$t('actions.updateAcquisition')"
        @click="confirmUpdateAcquisition"
        :disabled="!selectedOrganization || !selectedProduct || !selectedProductTier"
      />
    </div>
  </AcquisitionEditStyled>
</template>

<script>
import { styled } from '@egoist/vue-emotion'
import chroma from 'chroma-js'
import { DateTime } from 'luxon'

import { FlashMessages } from '@common/singletons'
import Button from '@/components/Atomic/Atoms/ButtonAtom.vue'
import AcquisitionManageMolecule from './Molecules/AcquisitionManageMolecule'

import ACQUISITION_QUERY from '#/graphql/acquisition/acquisition.gql'
import LIST_ORGANIZATIONS_QUERY from '#/graphql/acquisition/listOrganizationsWithAcquisition.gql'
import LIST_PRODUCTS_QUERY from '#/graphql/acquisition/listProductsWithTierLevel.gql'
import ACQUISITIONS_TYPES from '#/graphql/acquisition/listAcquisitionStatusEnum.gql'
import UPDATE_ONE_ACQUISITION from '#/graphql/acquisition/updateOneAcquisition.gql'

const AcquisitionEditStyled = styled('div')`
  margin: 2rem;
  padding: 1rem;
  background: ${p => chroma(p.theme.colors.white).alpha(0.1).css()};
  backdrop-filter: blur(7px);
  border-radius: 0.5rem;

  header {
    margin-bottom: 1.5rem;
  }

  .action {
    margin: 1rem 0;
    display: flex;
    justify-content: flex-end;
  }
`

export default {
  components: {
    AcquisitionEditStyled,
    AcquisitionManageMolecule,
    Button,
  },
  props: {},
  data() {
    return {
      organizations: [],
      products: [],
      acquisitionsTypes: [],
      selectedOrganizationId: null,
      selectedProductId: null,
      selectedProductTierId: null,
      selectedAcquisitionType: null,
    }
  },
  computed: {
    selectedOrganization() {
      return this.organizations.find(o => o.id === this.selectedOrganizationId)
    },
    selectedProduct() {
      return this.products.find(p => p.id === this.selectedProductId)
    },
    selectedProductTier() {
      return this.productsTierAvailable.find(t => t.id === this.selectedProductTierId)
    },
    tierLevelsAllreadyAcquiredByOrganization() {
      return this.selectedOrganization?.acquisitions?.map(a => a?.productTierLevel?.id)
    },
    productsTierAvailable() {
      if (!this.selectedProduct) {
        return []
      } else {
        return this.selectedProduct?.tierLevels.map(t => ({
          ...t,
          name: `Tier ${t.tierLevel}`,
        }))
      }
    },
    productsTierAvailableForOrganization() {
      return this.productsTierAvailable?.filter(t => !this.tierLevelsAllreadyAcquiredByOrganization?.includes(t.id))
    },
    productsTierNotAvailable() {
      return this.selectedProduct && this.productsTierAvailableForOrganization.length === 0
    },
    productsAvailable() {
      return this.products.map(p => ({
        ...p,
        name: `${p.name} (${p.type})`,
      }))
    },
  },
  methods: {
    confirmUpdateAcquisition() {
      this.$root.$emit(
        'activateOverlay',
        'ConfirmOverlay',
        {
          confirmText: `
            Are you sure you want to update Acquisition to, <p> 
            <b> ${this.selectedProductTier?.name} </b> of product 
            <b> ${this.selectedProduct?.name} </b> to  
            <b>${this.selectedOrganization?.name} </b> in <b>${this.selectedAcquisitionType}</b> state
          `,
          onConfirm: this.updateAcquisition,
          onConfirmArgs: {},
        },
        this.onCloseSettings,
      )
    },
    async updateAcquisition() {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_ONE_ACQUISITION,
          variables: {
            where: {
              id: this.$route.params.id,
            },
            data: {
              productTierLevel: {
                connect: {
                  id: this.selectedProductTierId,
                },
              },
              status: {
                set: this.selectedAcquisitionType,
              },
              organizationId: {
                set: this.selectedOrganizationId,
              },
              orderedAt: {
                set: this.selectedAcquisitionType === 'ORDERED' ? DateTime.now() : null,
              },
              trialStartedAt: {
                set: this.selectedAcquisitionType === 'TRIAL_STARTED' ? DateTime.now() : null,
              },
              purchasedAt: {
                set: this.selectedAcquisitionType === 'PURCHASED' ? DateTime.now() : null,
              },
            },
          },
          update: async (store, { data }) => {
            const acquisition = data.updateOneAcquisition
            const { organizations } = store.readQuery({
              query: LIST_ORGANIZATIONS_QUERY,
            })
            const organizationToUpdate = organizations.find(s => s.id === this.selectedOrganization.id)
            const acquisitionIndex = organizationToUpdate.acquisitions.findIndex(a => a.id === this.$route.params.id)
            organizationToUpdate.acquisitions[acquisitionIndex] = acquisition
            store.writeQuery({
              query: LIST_ORGANIZATIONS_QUERY,
              data: {
                organizations,
              },
            })
          },
        })
        this.selectedOrganizationId = null
        this.selectedProductId = null
        this.selectedProductTierId = null
        this.$root.$emit('closeOverlay')
        this.$emit('reload', res.id)
      } catch (err) {
        FlashMessages.$emit('error', err)
      }
    },
  },
  apollo: {
    acquisition: {
      query: ACQUISITION_QUERY,
      variables() {
        return {
          where: {
            id: this.$route.params.id,
          },
        }
      },
      result({ data }) {
        const acquisition = data.acquisition
        this.selectedOrganizationId = acquisition.organizationId
        this.selectedProductId = acquisition.productTierLevel.product.id
        this.selectedProductTierId = acquisition.productTierLevel.id
        this.selectedAcquisitionType = acquisition.status
      },
    },
    organizations: {
      query: LIST_ORGANIZATIONS_QUERY,
    },
    products: {
      query: LIST_PRODUCTS_QUERY,
    },
    acquisitionsTypes: {
      query: ACQUISITIONS_TYPES,
      update({ acquisitionsTypes }) {
        return !acquisitionsTypes?.enumValues ? [] : acquisitionsTypes?.enumValues.map(t => t.name)
      },
    },
  },
}
</script>
